import { Component, DestroyRef, Input, OnInit, inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { startWith, map, Observable } from 'rxjs';
import { DeleteModalComponent } from 'src/app/components/shared/delete-modal/delete-modal.component';
import { SettingsVariantsFormControl } from 'src/app/models/mastersuites/masterSuiteFormControl';
import { IMasterSuite, ISettingsVariant } from 'src/app/models/mastersuites/mastersuite';
import {
  SettingsVariantNew,
  SettingsVariantTouchNew,
  SettingsVariantTouchAssetNew,
} from 'src/app/models/mastersuites/mastersuiteNew';
import { ProblemDetails } from 'src/app/models/problem-details';
import { Setting } from 'src/app/models/settings/setting';
import { ValidationErrors } from 'src/app/models/validationErrors';
import { AlertService } from 'src/app/services/alert.service';
import { MasterSuitesService } from 'src/app/services/mastersuites.service';
import { SettingsService } from 'src/app/services/setting.service';

@Component({
    selector: 'app-settings-variant-content',
    templateUrl: './settings-variant-content.component.html',
    styleUrls: ['./settings-variant-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class SettingsVariantContentComponent implements OnInit {
  @Input() masterSuite!: IMasterSuite;
  private destroyRef = inject(DestroyRef);
  constructor(
    private settingsService: SettingsService,
    private masterSuitesService: MasterSuitesService,
    private alertService: AlertService,
    public dialog: MatDialog,
  ) {}
  serverErrorMessage: String = '';
  ngOnInit(): void {
    this.listSettingsVariants();
  }
  listSettingsVariants() {
    if (isDevMode()) {
      console.log('[DEV] [SettingsVariantContentComponent] this', this.masterSuite);
    }
    this.masterSuitesService
      .listSettingsVariants(this.masterSuite.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.settingsVariants = res.settingsVariants;
        this.settingsVariants.forEach((variant, i) => {
          this.initializeVariantFormState(variant, i);
        });
        this.listSettings();
      });
  }

  initializeVariantFormState(variant: ISettingsVariant, index: number) {
    this.readonly = [...this.readonly, true];
    this.displayed = [...this.displayed, false];
    this.settingsVariantsFormControl = [...this.settingsVariantsFormControl, {
      Name: new FormControl('', [Validators.required]),
      Setting: [],
    }];
    this.sortVariantTouches(variant);
    variant.settingsVariantTouches.forEach(() => {
      this.settingsVariantsFormControl[index].Setting = [
        ...this.settingsVariantsFormControl[index].Setting,
        new FormControl('', [Validators.required]),
      ];
    });
  }

  sortVariantTouches(variant: ISettingsVariant) {
    variant.settingsVariantTouches = [...variant.settingsVariantTouches].sort(
      (a, b) => a.masterSuiteTouchIndex - b.masterSuiteTouchIndex,
    );
  }

  errorMessages: ValidationErrors[] = [];
  validatorProperties: string[] = [];
  settingsVariantsFormControl: SettingsVariantsFormControl[] = [];
  settingsVariants: ISettingsVariant[] = [];
  settings: Setting[] = [];
  settingsFilteredOptions: Observable<Setting[]>[][] = [];
  settingsLoaded: boolean = false;
  displayed: boolean[] = [];
  readonly: boolean[] = [];
  _settingFilter(name: string): Setting[] {
    const filterValue = name.toLowerCase();
    return this.settings.filter((option) => option.name.toLowerCase().includes(filterValue));
  }
  displaySettingFn(settingId: string) {
    if (settingId) {
      return this.settings.find((_) => _.id === settingId)?.name!;
    } else {
      return '';
    }
  }
  parseSettingValue(value: any) {
    const name = typeof value === 'string' ? value : value?.name;
    return name ? this._settingFilter(name as string) : this.settings.slice();
  }
  listSettings() {
    this.settingsService
      .listSettings(this.masterSuite.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (settings) => {
          if (settings) {
            this.settings = settings;
            this.settingsVariants.forEach((variant, i) => {
              this.settingsFilteredOptions.push([]);
              variant.settingsVariantTouches.forEach((variantTouch, j) => {
                this.settingsFilteredOptions[i].push(
                  this.settingsVariantsFormControl[i].Setting[j].valueChanges.pipe(
                    startWith(''),
                    map((value) => this.parseSettingValue(value)),
                  ),
                );
              });
            });
            if (isDevMode()) {
              console.log('[DEV] [SettingsVariantContentComponent] this', this.settingsFilteredOptions);
            }
            this.settingsLoaded = true;
          }
        },
        (error) => {
          this.serverErrorMessage = 'There was an error loading the settings from the server.';
        },
      );
  }
  addVariant() {
    var variant = new SettingsVariantNew();
    const index = this.settingsVariants.length;
    this.readonly.push(false);
    this.displayed.push(true);
    this.settingsVariantsFormControl.push({
      Name: new FormControl('', [Validators.required]),
      Setting: [],
    });

    this.settingsFilteredOptions.push([]);

    this.masterSuite.masterSuiteTouches.forEach((touch, i) => {
      var variantTouch = new SettingsVariantTouchNew();
      variantTouch.masterSuiteTouchName = touch.name;
      variantTouch.masterSuiteTouchId = touch.id;
      variantTouch.masterSuiteTouchIndex = touch.index;
      this.settingsVariantsFormControl[index].Setting.push(
        new FormControl('', [Validators.required]),
      );
      this.settingsFilteredOptions[index].push(
        this.settingsVariantsFormControl[index].Setting[i].valueChanges.pipe(
          startWith(''),
          map((value) => this.parseSettingValue(value)),
        ),
      );
      variant.settingsVariantTouches.push(variantTouch);
    });
    this.settingsVariants.push(variant);
  }
  removeVariant(variant: ISettingsVariant, index: number, $event: any) {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        header: `Delete Variant: ${variant.name}`,
        message: 'Are you sure you want to delete this variant?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.masterSuitesService.deleteSettingsVariant(variant).subscribe({
            next: () => {
              this.settingsVariants.splice(index, 1);
              this.settingsVariantsFormControl.splice(index, 1);
              this.settingsFilteredOptions.splice(index, 1);
              this.readonly.splice(index, 1);
              this.alertService.successToast('Successfully Deleted Settings Variant');
            },
          });
        }
      });
  }
  editVariant(variant: ISettingsVariant, index: number) {
    this.displayed[index] = true;
    if (variant.settingsVariantTouches.length != this.masterSuite.masterSuiteTouches.length) {
      this.masterSuite.masterSuiteTouches.forEach((touch, i) => {
        var found = variant.settingsVariantTouches.find(
          (svt) => svt.masterSuiteTouchId == touch.id,
        );
        if (!found) {
          var variantTouch = new SettingsVariantTouchNew();
          variantTouch.masterSuiteTouchName = touch.name;
          variantTouch.masterSuiteTouchIndex = touch.index;
          variantTouch.masterSuiteTouchId = touch.id;
          this.settingsVariantsFormControl[index].Setting.splice(
            touch.index / 1000,
            0,
            new FormControl('', [Validators.required]),
          );
          this.settingsFilteredOptions[index].splice(
            touch.index / 1000,
            0,
            this.settingsVariantsFormControl[index].Setting[i].valueChanges.pipe(
              startWith(''),
              map((value) => this.parseSettingValue(value)),
            ),
          );
          variant.settingsVariantTouches.splice(touch.index / 1000, 0, variantTouch);
        }
      });
    }

    setTimeout(() => {
      this.readonly[index] = false;
    }, 100);
  }
  closeVariant(index: number) {
    this.displayed[index] = false;
    this.readonly[index] = true;
  }
  saveVariant(variant: ISettingsVariant, index: number) {
    if (variant.id == null) {
      variant.masterSuiteId = this.masterSuite.id!;
      this.masterSuitesService.createSettingsVariant(variant).subscribe(
        (res) => {
          const data = res.settingsVariant;
          if (isDevMode()) {
            console.log('[DEV] [SettingsVariantContentComponent] create settings variant: ', data);
          }
          this.applyCreateResult(variant, data, index);
        },
        (error) => {
          this.validateResult(error, index);
        },
      );
    } else {
      this.masterSuitesService.updateSettingsVariant(variant).subscribe(
        (data: ISettingsVariant) => {
          if (isDevMode()) {
            console.log('[DEV] [SettingsVariantContentComponent] update settings variant: ', data);
          }
          this.applyUpdateResult(variant, data, index);
        },
        (error) => {
          this.validateResult(error, index);
        },
      );
    }
  }

  applyCreateResult(variant: ISettingsVariant, data: ISettingsVariant, index: number) {
    this.alertService.successToast('Successfully Created Settings Variant');
    this.readonly[index] = true;
    variant.id = data.id;
    variant.version = data.version;
    variant.settingsVariantTouches.forEach((svt) => {
      const newSvt = data.settingsVariantTouches.find(
        (n) => n.masterSuiteTouchId == svt.masterSuiteTouchId,
      );
      svt.id = newSvt!.id;
    });
  }

  applyUpdateResult(variant: ISettingsVariant, data: ISettingsVariant, index: number) {
    this.alertService.successToast('Successfully Updated Settings Variant');
    this.readonly[index] = true;
    this.displayed[index] = false;
    variant.version = data.version;
    variant.settingsVariantTouches.forEach((svt) => {
      svt.version = data.settingsVariantTouches.find((x) => x.id == svt.id)!.version;
    });
  }
  cancelVariantCreate(variant: ISettingsVariant, index: number, $event: any) {
    if (variant.id == null) {
      $event.stopPropagation();
      this.settingsVariants.splice(index, 1);
      this.settingsVariantsFormControl.splice(index, 1);
      this.settingsFilteredOptions.splice(index, 1);
      this.readonly.splice(index, 1);
    } else {
      this.readonly[index] = true;
      variant.settingsVariantTouches.forEach((svt, i) => {
        if (svt.id == null) {
          variant.settingsVariantTouches.splice(i, 1);
          this.settingsVariantsFormControl[index].Setting.splice(i, 1);
          this.settingsFilteredOptions[index].splice(i, 1);
        }
      });
    }
  }

  validateResult(error: any, index: number) {
    const problem: ProblemDetails | undefined = error.error?.title ? error.error : undefined;
    if (problem?.errors) {
      this.errorMessages = [];
      this.validatorProperties = [];
      Object.entries(problem.errors).forEach(([property, messages]) => {
        const prefixedProperty = `MasterSuiteVariants[${index}].${property}`;
        const validationError: ValidationErrors = { property: prefixedProperty, message: messages[0] };
        this.errorMessages = [...this.errorMessages, validationError];
        this.validatorProperties = [...this.validatorProperties, prefixedProperty];
      });
    }

    this.settingsVariantsFormControl.forEach((form, i) => {
      form.Name.markAllAsTouched();
      if (
        this.errorMessages.find((error) => error.property == `MasterSuiteVariants[${i}].Name`)
      ) {
        form.Name.setErrors({ serverErrors: 'validation errors' });
      }
      form.Setting.forEach((settingForm, j) => {
        settingForm.markAllAsTouched();
        if (
          this.errorMessages.find(
            (error) =>
              error.property ==
              `MasterSuiteVariants[${i}].SettingsVariantTouch[${j}].Setting`,
          )
        ) {
          settingForm.setErrors({ serverErrors: 'validation errors' });
        }
      });
    });
  }

  validationMessage(property: string) {
    return this.errorMessages.find((error) => error.property == property)?.message;
  }


  viewSetting(id: string) {
    var url = `/settings/${id}/update`;
    window.open(url, '_blank');
  }
}
